import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useSprings, animated } from 'react-spring/web.cjs';
import { useDrag } from 'react-use-gesture';

import RowButton from 'components/Button/RowButton';
import { Link } from 'components/Common';

import defaultImage from 'images/PromoBg@2x.png';
import { fitbitGreen } from 'themes';
import { isiOS, bannerAutoRotateInterval } from 'config';
import fsc from 'fsc';

const StyledSlider = styled.div`
  position: relative;
  height: 250px;
`;

const BannerContainer = styled(animated.div)`
  position: absolute;
  height: 250px;
  width: 100vw;
  will-change: transform;
`;

const Dots = styled.div`
  position: absolute;
  left: 50%;
  bottom: ${isiOS ? 12 : 16}px;
  z-index: 3;
  transform: translate3d(-50%, 0, 0);
`;

const Dot = styled.div`
  display: inline-block;
  width: ${isiOS ? 5 : 7}px;
  height: ${isiOS ? 5 : 7}px;
  border-radius: 50%;
  margin: 0 ${isiOS ? 3.5 : 3}px;
  background: #ffffff;

  ${({ active }) =>
    active
      ? css`
          background: ${isiOS ? '#02b3bb' : '#00b0b9'};
        `
      : ''}
`;

const BannerContainerBackground = styled.div`
  background: url('${({ backgroundUrl }) =>
    backgroundUrl ? backgroundUrl : defaultImage}');
  background-size: cover;
  filter: brightness(80%);
  height: 250px;
  left: 0;
  position: absolute;
  top: 0;
  width: 100vw;
`;

const Content = styled.div`
  left: 50vw;
  position: absolute;
  text-align: center;
  top: 125px;
  transform: translate(-50%, -50%);
  width: 75vw;
`;

const CommonTextStyle = `
  color: white;
  text-shadow:
    -.1px -.1px 0 gray,
    .1px -.1px 0 gray,
    -.1px .1px 0 gray,
      .1px .1px 0 gray;
  -webkit-text-stroke: .1px gray;
`;

const Text = styled.div`
  ${CommonTextStyle}
  font-size: 14px;
  margin-top: 5px;
`;

const Title = styled.div`
  ${CommonTextStyle}
  font-size: 26px;
`;

const Button = styled(RowButton)`
  margin-top: 20px;
`;

const BannerLink = ({ link: { type, resourceId }, isClock, ...rest }) => {
  let to;

  if (type === 'URL') {
    to = resourceId;

    if (to && !to.startsWith('/')) {
      // External link
      /* eslint-disable jsx-a11y/anchor-has-content */
      return (
        <a
          href={resourceId}
          target="_blank"
          rel="noopener noreferrer"
          {...rest}
        />
      );
      /* eslint-enable jsx-a11y/anchor-has-content */
    }
  }

  const urlPrefix = isClock ? 'clock' : 'app';

  if (type === 'APP') {
    to = `/${urlPrefix}/${resourceId}`;
  } else if (type === 'CATEGORY') {
    to = `/${urlPrefix}s/category/${resourceId}`;
  } else if (type === 'COLLECTION') {
    to = `/${urlPrefix}s/collection/${resourceId}`;
  }

  return <Link to={to} pageDirection="FORWARD" {...rest} />;
};

const getNextIndex = (index, length) =>
  index < 0 ? length + index : index >= length ? index - length : index;

const computeStyles = ({ i, index, length, down, movement, forward }) => {
  const currentFirst = index === 0;
  const currentLast = index === length - 1;
  const isFirst = i === 0;
  const isLast = i === length - 1;
  const isCurrent = i === index;
  const isPrev = currentFirst ? isLast : i === index - 1;
  const isNext = currentLast ? isFirst : i === index + 1;

  let x;
  if (currentFirst && isLast) {
    x = -window.innerWidth;
  } else if (currentLast && isFirst) {
    x = window.innerWidth * (length - index);
  } else {
    x = window.innerWidth * (i - index);
  }

  x += down ? movement : 0;

  let visible = true;
  if (!(isPrev || isCurrent || isNext)) {
    visible = down;
  }

  let zIndex = 'initial';
  if (isCurrent) {
    zIndex = 2;
  } else if (forward ? isPrev : isNext) {
    zIndex = 1;
  }

  return {
    x,
    immediate: down,
    display: visible ? 'block' : 'none',
    zIndex
  };
};

export default ({ fscViewName, isClock, banners }) => {
  const [current, setCurrent] = useState(0);
  const [isDown, setDown] = useState(false);
  const [props, set] = useSprings(banners.length, (i) => ({
    x: i * window.innerWidth,
    display: 'block',
    zIndex: 'initial'
  }));

  const sendFcsViewEvent = (bannerIndex, bannerUri, clicked = true) => {
    const text = `${clicked ? '' : 'Viewed '}Promo Action`;
    fsc
      .view(fscViewName)
      .Tapped(text)
      .withPayload({
        bannerIndex,
        bannerUri
      })
      .send();
  };

  useEffect(() => {
    let id;

    function startNextBannerTimeout() {
      id = setTimeout(() => {
        if (!isDown) {
          setCurrent((current) => {
            const newCurrent = getNextIndex(current + 1, banners.length);
            set((i) =>
              computeStyles({
                i,
                index: newCurrent,
                length: banners.length,
                down: isDown,
                movement: 0,
                forward: true
              })
            );
            return newCurrent;
          });
          startNextBannerTimeout();
        }
      }, bannerAutoRotateInterval);
    }

    startNextBannerTimeout();

    return () => clearTimeout(id);
  }, [isDown, current, banners.length, set]);

  const bind = useDrag(
    ({ down, movement: [mx], direction: [xDir], distance }) => {
      setDown(down);

      let currentIndex = current;
      if (!down && distance > window.innerWidth / 4) {
        setCurrent((current) => {
          const computedIndex = current + (xDir > 0 ? -1 : 1);
          currentIndex = getNextIndex(computedIndex, banners.length);
          return currentIndex;
        });

        sendFcsViewEvent(
          currentIndex,
          banners[currentIndex].background.uri,
          false
        );
      }

      set((i) =>
        computeStyles({
          i,
          index: currentIndex,
          length: banners.length,
          down,
          movement: mx,
          forward: xDir < 0
        })
      );
    }
  );

  return (
    <StyledSlider>
      {banners.map(({ background, button, text, title }, i) => (
        <BannerContainer
          {...bind()}
          key={i}
          style={{
            display: props[i].display,
            transform: props[i].x.interpolate(
              (x) => `translate3d(${x}px, 0, 0)`
            ),
            zIndex: props[i].zIndex
          }}
        >
          <BannerContainerBackground backgroundUrl={background.uri} />
          <Content>
            {title && <Title>{title}</Title>}
            {text && <Text>{text}</Text>}
            {button && button.text && button.link && (
              <BannerLink
                isClock={isClock}
                link={button.link}
                onClick={() => sendFcsViewEvent(i, background.uri)}
              >
                <Button bgColor="white" color={fitbitGreen} minWidth="124px">
                  {button.text}
                </Button>
              </BannerLink>
            )}
          </Content>
        </BannerContainer>
      ))}
      <Dots>
        {banners.map((_, i) => (
          <Dot key={i} active={i === current} />
        ))}
      </Dots>
    </StyledSlider>
  );
};
